import type { NavigateFunction } from 'react-router-dom';
import type { IFullPageOrigin } from '@/features/work-items/fullPageOrigin';

/**
 * I-1304 (`docs/concept-iphone-ux.md`, *Navigation*, *An internal link opens as a list tap does*):
 * the one way to open a page of the app, and what counts as one.
 *
 * A list row is a router link. A link in text — a key citation, an authored link, a References row —
 * is a real anchor that asks for a new window (I-1061), and in the app that request became a
 * document load: a cold open with no Back to the linking page. `InternalLinkClicks` catches the
 * tap and calls `openInternalAddress`, the same router push a list row makes.
 */

/** What the route table says of a pathname, as far as following a link needs (`shellRouteOf`). */
export type RouteHandlingOfType = (pathname: string) => { readonly handling: string };

/**
 * `href` as an address of this app — its path, query and fragment — or `null` for anything else.
 *
 * Internal is this origin and a path the route table marks in-app (`routeOf`, the table's
 * `shellRouteOf`): the API, a download, a sign-in page and another origin are not. A full URL to this
 * deployment is internal, as the relative form of the same address is. The table is passed in, so
 * this file holds none of it (I-2453): `internalAddressOf.ts` reads it statically for a screen that
 * draws links, and `internalAddressLoad.ts` loads it after the launch for the click handler.
 */
export function internalAddressWith(
  routeOf: RouteHandlingOfType,
  href: string | null | undefined,
  origin: string = window.location.origin,
): string | null {
  if (!href) return null;
  let url: URL;
  try {
    url = new URL(href, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  if (routeOf(url.pathname).handling !== 'in-app') return null;
  return `${url.pathname}${url.search}${url.hash}`;
}

/**
 * I-1304 (IL-16): the mark a link's push leaves on its own history entry, with the page it was
 * tapped on. A root reached this way has a page of the reader's own under it, and draws the back
 * row; opened from More or as a tab it has none. On the entry, so a reload of it keeps the mark and
 * no later navigation inherits it.
 */
export interface ILinkOpenState {
  readonly openedBy: 'link';
  /** The linking page's address. */
  readonly from: string;
}

export function linkOpenState(from: string): ILinkOpenState {
  return { openedBy: 'link', from };
}

/** The mark, if this entry was pushed by a link. */
export function linkOpenOf(state: unknown): ILinkOpenState | null {
  if (typeof state !== 'object' || state === null) return null;
  const { openedBy, from } = state as Partial<ILinkOpenState>;
  return openedBy === 'link' && typeof from === 'string' ? { openedBy, from } : null;
}

/**
 * Open a page of the app: a router push, so the page it was opened from is one Back away. `cameFrom`
 * is a full page's origin (`fullPageOrigin.ts`), which holds the reader's scope (I-1357, IL-19f).
 */
export function openInternalAddress(
  navigate: NavigateFunction,
  address: string,
  { cameFrom }: { cameFrom?: IFullPageOrigin } = {},
): void {
  const from = `${window.location.pathname}${window.location.search}`;
  void navigate(address, {
    state: cameFrom ? { ...linkOpenState(from), cameFrom } : linkOpenState(from),
  });
}
